import { FC } from 'react';
import { Input, Popover, Button, Space, theme } from 'antd';
import { PhoneOutlined } from '@ant-design/icons';
import { TelephoneNumber } from '#types';
import { useTelephoneUI } from './useTelephoneUI';
import DetailedTelephoneForm from './DetailedTelephoneForm';
import { usePdfExportContext } from '@/components/pdfExport/context/PdfExportContext';

/**
 * Props for the TelephoneUI component
 */
interface TelephoneUIProps {
    /** Current telephone value (string or structured TelephoneValue) */
    value?: string | TelephoneNumber | null | undefined;
    /** Callback when telephone changes */
    onChange?: (value: TelephoneNumber) => void;
    /** Whether the input is disabled */
    disabled?: boolean;
    /** Validation status for the input */
    status?: '' | 'error' | 'warning' | undefined;
    /** Additional props passed to Input */
    [key: string]: any;
}

/**
 * Telephone input component with intelligent parsing and detailed manual entry form.
 * Provides both quick input and detailed component editing via a popover.
 */
const TelephoneUI: FC<TelephoneUIProps> = ({
    value,
    onChange = () => {},
    disabled = false,
    status,
    ...props
}) => {
    const { style, variant, ...restProps } = props;
    const { isPdfExportMode } = usePdfExportContext();
    const {
        telephoneValue,
        inputValue,
        popoverOpen,
        setPopoverOpen,
        handleInputChange,
        handleBlur,
        handleClear,
        handleComponentChange
    } = useTelephoneUI({ value, onChange });

    const { token } = theme.useToken();

    return (
        <Space.Compact style={{ width: '100%', ...style }}>
            <Input
                aria-label="telephone-input"
                {...restProps}
                value={inputValue}
                onChange={(e) => handleInputChange(e.target.value)}
                onClear={handleClear}
                onBlur={handleBlur}
                disabled={disabled}
                status={status}
                allowClear
                placeholder="Enter phone number..."
                style={{ flex: 1, minWidth: 0, width: '100%' }}
                variant={variant}
            />
            {!isPdfExportMode && (
                <Popover
                    content={
                        <DetailedTelephoneForm
                            telephoneValue={telephoneValue}
                            disabled={disabled}
                            status={status}
                            onComponentChange={handleComponentChange}
                        />
                    }
                    trigger="click"
                    open={popoverOpen}
                    onOpenChange={setPopoverOpen}
                    placement="bottomRight"
                >
                    <Button
                        type="text"
                        style={{ width: token.controlHeight }}
                        icon={<PhoneOutlined />}
                        disabled={disabled}
                        aria-label="telephone-details-button"
                    />
                </Popover>
            )}
        </Space.Compact>
    );
};

export default TelephoneUI;

